import type { Locale, ServicePage } from "../content/types"
import Breadcrumbs from "../components/Breadcrumbs"
import PageCta from "../components/PageCta"
import JsonLd from "../components/seo/JsonLd"
import { RelatedCases, RelatedArticles } from "../components/RelatedContent"
import { buildServiceSchema } from "../seo/schema/service"
import { SITE_URL } from "../content/site"

interface ServicePageTemplateProps {
  service: ServicePage
  locale: Locale
}

/**
 * Shared template for every service detail page.
 * Sections render from structured content; lengths may differ per service.
 */
export default function ServicePageTemplate({ service, locale }: ServicePageTemplateProps) {
  const servicesIndexPath = locale === "nl" ? "/nl/diensten/" : "/en/services/"
  const servicePath = `${servicesIndexPath}${service.slug[locale]}`
  const pageTitle = service.title[locale].split("|")[0].trim()

  return (
    <div className="min-h-screen bg-site-bg">
      <JsonLd data={buildServiceSchema(service, locale, `${SITE_URL}${servicePath}`)} />

      <div className="max-w-7xl mx-auto px-4 pt-12 pb-24">
        {/* Hero */}
        <header className="mb-16 max-w-3xl">
          <Breadcrumbs
            locale={locale}
            items={[
              { label: locale === "nl" ? "Diensten" : "Services", path: servicesIndexPath },
              { label: pageTitle },
            ]}
          />
          <h1 className="text-4xl md:text-5xl font-bold text-text-primary mt-8 mb-6">{service.h1?.[locale]}</h1>
          <p className="text-xl text-text-secondary leading-relaxed">{service.shortDescription[locale]}</p>
        </header>

        {/* Intro */}
        <section className="mb-16 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6">{service.intro.heading[locale]}</h2>
          <p className="text-lg text-text-secondary leading-relaxed">{service.intro.body[locale]}</p>
        </section>

        {/* Capabilities */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-8">
            {locale === "nl" ? "Wat we doen" : "What we do"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {service.capabilities.map((capability) => (
              <div key={capability.title[locale]} className="bg-card-bg rounded-2xl p-6 border border-border-subtle">
                <h3 className="text-xl font-bold text-text-primary mb-3">{capability.title[locale]}</h3>
                <p className="text-text-secondary leading-relaxed">{capability.description[locale]}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-8">
          {/* Suitable for */}
          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6">
              {locale === "nl" ? "Wanneer is dit geschikt?" : "When is this right for you?"}
            </h2>
            <ul className="space-y-4">
              {service.suitableFor.map((item) => (
                <li key={item[locale]} className="flex items-start gap-3 text-text-secondary leading-relaxed">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-button-secondary flex items-center justify-center mt-0.5">
                    <svg className="w-3.5 h-3.5 text-button-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  {item[locale]}
                </li>
              ))}
            </ul>
          </section>

          {/* Process */}
          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6">
              {locale === "nl" ? "Hoe we werken" : "How we work"}
            </h2>
            <ol className="space-y-6">
              {service.process.map((step, index) => (
                <li key={step.title[locale]} className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-button-primary text-white font-bold flex items-center justify-center text-sm">
                    {index + 1}
                  </span>
                  <div>
                    <h3 className="font-bold text-text-primary">{step.title[locale]}</h3>
                    <p className="text-text-secondary text-sm leading-relaxed mt-1">{step.description[locale]}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>

        {/* FAQ: visible content only (no FAQ markup without it) */}
        {service.faq && service.faq.length > 0 && (
          <section className="mb-16 max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-6">
              {locale === "nl" ? "Veelgestelde vragen" : "Frequently asked questions"}
            </h2>
            <dl className="space-y-6">
              {service.faq.map((item) => (
                <div key={item.question[locale]}>
                  <dt className="text-lg font-bold text-text-primary mb-2">{item.question[locale]}</dt>
                  <dd className="text-text-secondary leading-relaxed">{item.answer[locale]}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        <RelatedCases locale={locale} ids={service.relatedCaseIds} />
        <RelatedArticles locale={locale} ids={service.relatedKnowledgeIds} />
        <PageCta locale={locale} />
      </div>
    </div>
  )
}
